import { useEffect } from "react";

const DEFAULT_URL = "https://hookora.app";
const DEFAULT_LOGO = `${DEFAULT_URL}/og-image.png`;

function StructuredData({
  data,
  id = "structured-data"
}) {
  useEffect(() => {
    if (!data) return undefined;

    let script = document.getElementById(id);

    if (!script) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = id;
      document.head.appendChild(script);
    }

    script.textContent = JSON.stringify(data);

    return () => {
      const existing = document.getElementById(id);

      if (existing) {
        existing.remove();
      }
    };
  }, [data, id]);

  return null;
}

/* ---------------------------------- */
/* ORGANIZATION SCHEMA */
/* ---------------------------------- */

export const buildOrganizationSchema = ({
  name = "Hookora",
  url = DEFAULT_URL,
  logo = DEFAULT_LOGO,
  sameAs = []
} = {}) => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  name,
  url,
  logo,
  sameAs
});

/* ---------------------------------- */
/* SOFTWARE APPLICATION SCHEMA */
/* ---------------------------------- */

export const buildSoftwareApplicationSchema = ({
  name = "Hookora",
  description = "AI hook workflow platform for creators that helps generate hooks, titles, captions, hashtags, descriptions, scripts, and thumbnail text.",
  url = DEFAULT_URL,
  category = "BusinessApplication",
  operatingSystem = "Web",
  price = "0",
  priceCurrency = "USD"
} = {}) => ({
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name,
  applicationCategory: category,
  operatingSystem,
  description,
  url,
  offers: {
    "@type": "Offer",
    price,
    priceCurrency
  }
});

/* ---------------------------------- */
/* ARTICLE SCHEMA */
/* ---------------------------------- */

export function buildArticleSchema({
  headline,
  description,
  image,
  url,
  keywords = [],
  datePublished,
  dateModified,
  author = "Hookora Team"
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline,
    description,
    image,
    mainEntityOfPage: url,
    datePublished,
    dateModified,
    author: {
      "@type": "Organization",
      name: author
    },
    publisher: {
      "@type": "Organization",
      name: "Hookora",
      logo: {
        "@type": "ImageObject",
        url: `${DEFAULT_URL}/logo.png`
      }
    },
    keywords: keywords.join(", ")
  };
}

/* ---------------------------------- */
/* BREADCRUMB SCHEMA */
/* ---------------------------------- */

export function buildBreadcrumbSchema(
  items = []
) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map(
      (item, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: item.name,
        item:
          item.url ||
          `${DEFAULT_URL}${item.to || ""}`
      })
    )
  };
}

/* ---------------------------------- */
/* FAQ SCHEMA */
/* ---------------------------------- */

export const buildFAQSchema = (
  faqs = []
) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((faq) => ({
    "@type": "Question",
    name: faq.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: faq.answer
    }
  }))
});

/* ---------------------------------- */
/* WEBPAGE SCHEMA */
/* ---------------------------------- */

export const buildWebPageSchema = ({
  name,
  description,
  url,
  type = "WebPage"
}) => ({
  "@context": "https://schema.org",
  "@type": type,
  name,
  description,
  url
});

export default StructuredData;